import { z } from 'zod';
import { idSchema, localDateSchema, markdownSchema, type RecordLink, recordLinkSchema, versionSchema } from './common';

/**
 * Zadania (docs/3VTOS_MVP.md §6, docs/DATA_MODEL.md „work”): `tasks`, `task_checklist_items`,
 * `task_proposals`, `workspace_counters` (packages/db/src/schema/work.ts). Moduł uprawnień: `tasks`.
 *
 * Zadanie: klient, projekt i usługa opcjonalne, ale spójne (projekt/usługa innego klienta = błąd; baza
 * pilnuje tego złożonym FK). Numer zadania (`#123`) unikalny w workspace: `nextCounter(tx, 'tasks')`
 * z `@fos/core/counters`. Wejścia i wyjścia usług dopisuje agent modułu work.
 */

/**
 * Statusy zadania. `request` = „Zgłoszenie” (DECISIONS 2026-10-17 [P] „Model portalu klienta”): zgłoszenie z portalu
 * klienta, kolumna Kanbanu „Zgłoszenia od klientów” przed „Do zrobienia”; przeniesienie do innej kolumny = przyjęcie.
 */
export const TASK_STATUSES = ['request', 'todo', 'in_progress', 'blocked', 'review', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const taskStatusSchema = z.enum(TASK_STATUSES);
/** Statusy otwarte (nie „Gotowe”). Zgłoszenie jest otwarte, ale bez wykonawcy (nie wpada w „Moje”). */
export const OPEN_TASK_STATUSES = [
  'request',
  'todo',
  'in_progress',
  'blocked',
  'review',
] as const satisfies readonly TaskStatus[];
/** Status zgłoszenia z portalu klienta. */
export const TASK_REQUEST_STATUS = 'request' as const satisfies TaskStatus;

/**
 * Przejścia statusu (17.10.2026): ze „Zgłoszenia” do każdego statusu (przyjęcie); do „Zgłoszenia” tylko
 * z „Do zrobienia” (cofnięcie przyjęcia). Pozostałe statusy między sobą — bez ograniczeń.
 */
export function canChangeTaskStatus(from: TaskStatus, to: TaskStatus): boolean {
  if (from === to) return true;
  if (to === 'request') return from === 'todo';
  return true;
}

export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);

/** Pochodzenie zadania (MVP §6: zawsze zapisujemy pochodzenie i link do źródła w `originRef`). */
export const TASK_ORIGINS = ['manual', 'message', 'meeting', 'ai', 'mcp', 'proposal', 'client_portal'] as const;
export type TaskOrigin = (typeof TASK_ORIGINS)[number];
export const taskOriginSchema = z.enum(TASK_ORIGINS);

/** Stan pracy AI nad zadaniem (DESIGN §2.5: AI pracuje, Wynik AI czeka, Czeka na akceptację, Gotowe). */
export const TASK_AI_STATES = ['none', 'proposed', 'running', 'review', 'waiting_approval', 'done'] as const;
export type TaskAiState = (typeof TASK_AI_STATES)[number];
export const taskAiStateSchema = z.enum(TASK_AI_STATES);

export const TASK_PROPOSAL_SOURCES = ['meeting', 'message', 'ai'] as const;
export type TaskProposalSource = (typeof TASK_PROPOSAL_SOURCES)[number];
export const taskProposalSourceSchema = z.enum(TASK_PROPOSAL_SOURCES);

export const TASK_PROPOSAL_STATUSES = ['proposed', 'accepted', 'rejected'] as const;
export type TaskProposalStatus = (typeof TASK_PROPOSAL_STATUSES)[number];
export const taskProposalStatusSchema = z.enum(TASK_PROPOSAL_STATUSES);

/** Nazwy liczników `workspace_counters` (numeracja per workspace). Inne moduły dopisują swoje. */
export const COUNTER_NAMES = {
  tasks: 'tasks',
  billingDrafts: 'billing_drafts',
  offers: 'offers',
  agentRuns: 'agent_runs',
} as const;
export const counterNameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_.:-]{0,62}$/, { message: 'Nieprawidłowa nazwa licznika' });

/** Pozycja w kolumnie Kanban / liście (numeric w bazie, string w TS, np. "1024" albo "1536.5"). */
export const positionSchema = z.string().regex(/^-?\d{1,12}(\.\d{1,10})?$/, { message: 'Nieprawidłowa pozycja' });

export const taskTitleSchema = z
  .string()
  .trim()
  .min(1, { message: 'Tytuł jest wymagany' })
  .max(300, { message: 'Najwyżej 300 znaków' });

/** Pola zadania edytowalne przez użytkownika (numer, pozycję i znaczniki ustawia usługa). */
export const taskFieldsSchema = z.object({
  title: taskTitleSchema,
  descriptionMd: markdownSchema.nullable().optional(),
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  serviceId: idSchema.nullable().optional(),
  assigneeUserId: idSchema.nullable().optional(),
  status: taskStatusSchema.default('todo'),
  priority: taskPrioritySchema.default('normal'),
  dueDate: localDateSchema.nullable().optional(),
});
export type TaskFields = z.infer<typeof taskFieldsSchema>;

/** Pochodzenie (ustawia usługa / adapter: wiadomość, spotkanie, AI, MCP, propozycja). */
export const taskOriginFieldsSchema = z.object({
  origin: taskOriginSchema.default('manual'),
  originRef: recordLinkSchema.nullable().optional(),
});
export type TaskOriginFields = z.infer<typeof taskOriginFieldsSchema>;

export const checklistItemTextSchema = z
  .string()
  .trim()
  .min(1, { message: 'Treść jest wymagana' })
  .max(500, { message: 'Najwyżej 500 znaków' });

export const checklistItemFieldsSchema = z.object({
  taskId: idSchema,
  text: checklistItemTextSchema,
  done: z.boolean().default(false),
});
export type ChecklistItemFields = z.infer<typeof checklistItemFieldsSchema>;

/** Propozycja zadania (spotkanie, wiadomość, AI); `dedupKey` = brak duplikatów przy ponowieniu przetwarzania. */
export const taskProposalFieldsSchema = z.object({
  sourceType: taskProposalSourceSchema,
  sourceId: idSchema,
  dedupKey: z.string().min(1).max(200),
  title: taskTitleSchema,
  description: markdownSchema.nullable().optional(),
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  assigneeUserId: idSchema.nullable().optional(),
  dueDate: localDateSchema.nullable().optional(),
});
export type TaskProposalFields = z.infer<typeof taskProposalFieldsSchema>;

// ── Usługi modułu work (Fala 1): wejścia i kształty odpowiedzi ──────────────

/** Filtr terminu: po terminie (otwarte), dziś, ten tydzień (od dziś do niedzieli), bez terminu. */
export const TASK_DUE_FILTERS = ['overdue', 'today', 'week', 'none'] as const;
export type TaskDueFilter = (typeof TASK_DUE_FILTERS)[number];

/** Sortowanie listy: termin (domyślnie), priorytet, ostatnia zmiana, najnowsze, kolejność z tablicy. */
export const TASK_SORTS = ['due', 'priority', 'updated', 'created', 'position'] as const;
export type TaskSort = (typeof TASK_SORTS)[number];

/**
 * „Moje” = przypisane do mnie (albo moje bez wykonawcy); „Zespół” = wszystko w moim zakresie danych. Ekran Zadania
 * dokłada do „Moje” zadania zlecone innym (`delegated: 'include'`, 10.10.2026); Dziś, Copilot i MCP — bez zmian.
 */
export const TASK_SCOPES = ['mine', 'team'] as const;
export type TaskScope = (typeof TASK_SCOPES)[number];
export const taskScopeSchema = z.enum(TASK_SCOPES);
export const TASK_DELEGATED_FILTERS = ['include', 'only'] as const;
export type TaskDelegatedFilter = (typeof TASK_DELEGATED_FILTERS)[number];

/**
 * Kolumny tablicy Kanban (MVP §6, Figma 04 Zadania / Tablica); „Gotowe” = licznik tygodnia i strefa upuszczenia.
 * `request` („Zgłoszenia od klientów”, 17.10.2026) pokazujemy, gdy `TaskBoardResult.requestsEnabled` albo kolumna ma
 * zgłoszenia (`total > 0`).
 */
export const BOARD_STATUSES = [
  'request',
  'todo',
  'in_progress',
  'blocked',
  'review',
] as const satisfies readonly TaskStatus[];

/** Odstęp pozycji w kolumnie (nowe zadanie = ostatnia + krok). */
export const POSITION_STEP = 1024;

/** Komunikaty spójności powiązań i wykonawcy (odbiór Etapu 1). */
export const TASK_MESSAGES = {
  projectOtherClientChangeProject: 'Projekt należy do innego klienta — zmień też projekt',
  projectOtherClientChangeClient: 'Projekt należy do innego klienta — zmień też klienta',
  projectOtherClient: 'Projekt należy do innego klienta',
  internalProjectChangeProject: 'Projekt wewnętrzny nie ma klienta — zmień też projekt',
  internalProjectChangeClient: 'Projekt wewnętrzny nie ma klienta — usuń klienta zadania',
  internalProject: 'Projekt wewnętrzny nie może mieć klienta',
  serviceOtherClientChangeService: 'Usługa należy do innego klienta — zmień też usługę',
  serviceOtherClientChangeClient: 'Usługa należy do innego klienta — zmień też klienta',
  serviceOtherClient: 'Usługa należy do innego klienta',
  archivedClient: 'Klient jest zarchiwizowany',
  archivedProject: 'Projekt jest zarchiwizowany',
  archivedService: 'Usługa jest zarchiwizowana',
  assigneeNotMember: 'Ta osoba nie jest aktywnym członkiem workspace’u',
  assigneeNoModule: 'Ta osoba nie ma dostępu do modułu Zadania',
  assigneeNoScope: 'Wykonawca nie ma dostępu do klienta ani projektu tego zadania',
  archivedTask: 'Zadanie jest zarchiwizowane. Przywróć je, aby edytować.',
  neighbourNotInColumn: 'Wskazane zadanie nie leży w tej kolumnie',
  /** 17.10.2026: zgłoszenia z portalu klienta */
  requestOnlyFromTodo: 'Do „Zgłoszeń od klientów” można przenieść tylko zadanie z „Do zrobienia”',
  requestFromPortalOnly: 'Status „Zgłoszenie” mają zgłoszenia z portalu klienta — wybierz inny status',
  assigneeClient: 'Osoba z portalu klienta nie może być wykonawcą zadania',
  portalRequestClientLocked: 'Zgłoszenie z portalu zostaje przy kliencie, który je złożył — klienta nie można zmienić',
  clientMessagesPortalOnly: 'Rozmowa z klientem jest dostępna tylko przy zgłoszeniach z portalu klienta',
} as const;

/** Filtry wspólne dla listy i tablicy. `null` w polach powiązań = „bez” (np. zadania bez klienta). */
export const taskFilterSchema = z.object({
  scope: taskScopeSchema.default('team'),
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  serviceId: idSchema.nullable().optional(),
  /** `null` = bez wykonawcy */
  assigneeUserId: idSchema.nullable().optional(),
  /**
   * 10.10.2026 (Zadania → „Moje” i „Zlecone przeze mnie”): `include` = przy „Moje” także zadania, które dodałem i przypisałem
   * innym; `only` = tylko takie (filtr „Zlecone przeze mnie”, niezależnie od `scope`). Brak = „Moje” jak dotąd (Dziś, Copilot).
   */
  delegated: z.enum(TASK_DELEGATED_FILTERS).optional(),
  status: z.array(taskStatusSchema).max(TASK_STATUSES.length).optional(),
  /** kilka wartości = suma (np. ['overdue', 'today'] = „Dziś” na mobile) */
  due: z.array(z.enum(TASK_DUE_FILTERS)).max(TASK_DUE_FILTERS.length).optional(),
  /** tytuł (bez polskich znaków) albo numer („#12”, „12”) */
  q: z.string().trim().max(200).optional(),
  /** true = tylko zarchiwizowane */
  archived: z.boolean().default(false),
});
export type TaskFilter = z.input<typeof taskFilterSchema>;

export const listTasksInput = taskFilterSchema
  .extend({
    sort: z.enum(TASK_SORTS).default('due'),
    limit: z.number().int().min(1).max(500).default(200),
  })
  .default({});
export type ListTasksInput = z.input<typeof listTasksInput>;

export const boardTasksInput = taskFilterSchema
  .omit({ status: true, archived: true })
  .extend({ limitPerColumn: z.number().int().min(1).max(200).default(100) })
  .default({});
export type BoardTasksInput = z.input<typeof boardTasksInput>;

export const taskIdInput = z.object({ id: idSchema });

/**
 * Publiczne wejście (UI, AI, MCP): bez `origin` i `originRef`. Pochodzenie wynika z kanału aktora (UI = „Ręcznie”,
 * AI = „Od AI”, MCP = „Przez MCP”); nieznane pola są odrzucane. Zadanie ze źródła (wiadomość, spotkanie, propozycja)
 * zakłada moduł źródła wewnętrzną funkcją `createTaskFromSource` (@fos/core/modules/work), która sprawdza źródło.
 */
export const createTaskInput = taskFieldsSchema.strict();
export type CreateTaskInput = z.input<typeof createTaskInput>;

/** Edycja: tylko podane pola; zmiana klienta zadania z projektem/usługą innego klienta wymaga zmiany obu pól naraz. */
export const updateTaskInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    title: taskTitleSchema.optional(),
    descriptionMd: markdownSchema.nullable().optional(),
    clientId: idSchema.nullable().optional(),
    projectId: idSchema.nullable().optional(),
    serviceId: idSchema.nullable().optional(),
    assigneeUserId: idSchema.nullable().optional(),
    status: taskStatusSchema.optional(),
    priority: taskPrioritySchema.optional(),
    dueDate: localDateSchema.nullable().optional(),
  })
  .refine((v) => Object.entries(v).some(([k, val]) => k !== 'id' && k !== 'version' && val !== undefined), {
    message: 'Brak zmian do zapisania',
  });
export type UpdateTaskInput = z.input<typeof updateTaskInput>;

/**
 * Kanban: status (kolumna) + miejsce. `afterId` = zadanie, za którym ląduje karta (`null` = na górze kolumny).
 * `version` opcjonalna: podana i nieaktualna = CONFLICT.
 */
export const moveTaskInput = z.object({
  id: idSchema,
  version: versionSchema.optional(),
  status: taskStatusSchema,
  afterId: idSchema.nullable().default(null),
});
export type MoveTaskInput = z.input<typeof moveTaskInput>;

export const archiveTaskInput = z.object({ id: idSchema, version: versionSchema });

export const addChecklistItemInput = z.object({ taskId: idSchema, text: checklistItemTextSchema });
export const toggleChecklistItemInput = z.object({ id: idSchema, done: z.boolean() });
export const updateChecklistItemInput = z.object({ id: idSchema, text: checklistItemTextSchema });
/** `afterId` = element, za którym trafia przesuwany (`null` = na początek). */
export const reorderChecklistItemInput = z.object({ id: idSchema, afterId: idSchema.nullable().default(null) });
export const removeChecklistItemInput = z.object({ id: idSchema });

export const todayPlanInput = z.object({ scope: taskScopeSchema.default('mine') }).default({});
export type TodayPlanInput = z.input<typeof todayPlanInput>;

export const taskFormOptionsInput = z.object({}).strict().optional();

// ── Kształty odpowiedzi (DTO) ────────────────────────────────────────────────

export interface TaskPerson {
  userId: string;
  name: string;
  initials: string;
  avatarColor: string | null;
}

export interface TaskSummary {
  id: string;
  number: number;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  /** "YYYY-MM-DD" (Europe/Warsaw) */
  dueDate: string | null;
  position: string;
  aiState: TaskAiState;
  origin: TaskOrigin;
  client: { id: string; name: string; color: string | null } | null;
  project: { id: string; name: string; isInternal: boolean } | null;
  /** null także wtedy, gdy osoba nie widzi usług klienta (tryb częściowy) */
  service: { id: string; name: string } | null;
  assignee: TaskPerson | null;
  /** kto dodał zadanie (10.10.2026: etykieta „zlecone” przy zadaniach przypisanych innym) */
  createdByUserId: string | null;
  checklist: { done: number; total: number };
  archivedAt: Date | null;
  completedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  version: number;
  /** czy osoba może edytować (UI ukrywa akcje; backend i tak sprawdza) */
  canEdit: boolean;
}

export interface TaskChecklistItemDto {
  id: string;
  text: string;
  done: boolean;
  position: string;
  version: number;
}

export interface TaskHistoryChange {
  field: string;
  /** etykieta pola po polsku („Status”, „Termin”) */
  label: string;
  /** wartości gotowe do wyświetlenia (etykiety, nazwy, daty); null = brak */
  from: string | null;
  to: string | null;
}

export interface TaskHistoryEntry {
  id: string;
  at: Date;
  /** task.create | task.update | task.move | task.archive | task.restore | task.checklist.* */
  action: string;
  actor: TaskPerson | null;
  actorType: string;
  via: string;
  changes: TaskHistoryChange[];
  /** np. treść pozycji checklisty */
  detail: string | null;
}

export interface TaskDetail extends TaskSummary {
  descriptionMd: string | null;
  originRef: RecordLink | null;
  /**
   * Wiadomość źródłowa (`originRef` typu `message`, Fala 4): wątek do linku „Z maila: …”, nadawca i data maila. null =
   * zadanie nie z maila albo wiadomość poza zakresem osoby / bez modułu Inbox.
   */
  originMessage: { threadId: string; subject: string; from: string; sentAt: Date } | null;
  createdBy: TaskPerson | null;
  checklistItems: TaskChecklistItemDto[];
  history: TaskHistoryEntry[];
  /**
   * Zgłoszenie z portalu klienta: tytuł i opis, które widzi klient — wersja z chwili złożenia (przegląd portalu
   * 17.10.2026; zmiany zespołu, AI i MCP zostają wewnętrzne). null / brak = zadanie spoza portalu.
   */
  clientRequest?: { title: string; descriptionMd: string | null } | null;
}

export interface TaskListResult {
  items: TaskSummary[];
  /** true = więcej wyników niż `limit` */
  truncated: boolean;
}

export interface TaskBoardColumn {
  status: TaskStatus;
  items: TaskSummary[];
  total: number;
  truncated: boolean;
}

export interface TaskBoardResult {
  columns: TaskBoardColumn[];
  /**
   * Kolumna „Zgłoszenia od klientów” włączona: workspace ma choć jedną osobę w portalu klienta (aktywną albo
   * zaproszoną) albo choć jedno zgłoszenie. Kolumna `request` jest zawsze w `columns`; UI ukrywa ją, gdy false i pusta.
   */
  requestsEnabled: boolean;
  /** „12 gotowych w tym tygodniu” (od poniedziałku, Europe/Warsaw) */
  doneThisWeek: number;
}

export interface TodayPlanResult {
  today: TaskSummary[];
  overdue: TaskSummary[];
  counts: { today: number; overdue: number };
}

export interface TaskFormOptions {
  /** `partial` = klient widoczny tylko jako karta (zadanie tylko w jego projekcie z mojego zakresu) */
  clients: { id: string; name: string; color: string | null; partial: boolean }[];
  projects: { id: string; name: string; clientId: string | null; isInternal: boolean; status: string }[];
  services: { id: string; name: string; clientId: string; kind: string }[];
  /** aktywni członkowie; `canTasks` = mają dostęp do modułu Zadania */
  members: (TaskPerson & { canTasks: boolean })[];
}

// ── Rozmowa z klientem przy zgłoszeniu (17.10.2026, `task_client_messages`) ──
/**
 * Wiadomości widoczne dla klienta w portalu (zgłoszenie = zadanie z `origin = 'client_portal'`). Komentarze wewnętrzne
 * (`task.comment` w historii) zostają niewidoczne dla klienta. Wiadomość do klienta pisze tylko człowiek w aplikacji
 * (usługa z `denyVia: ['ai', 'mcp']`).
 */
export const TASK_CLIENT_MESSAGE_MAX_CHARS = 4000;
export const taskClientMessageTextSchema = z
  .string()
  .trim()
  .min(1, { message: 'Wpisz treść wiadomości' })
  .max(TASK_CLIENT_MESSAGE_MAX_CHARS, { message: `Najwyżej ${TASK_CLIENT_MESSAGE_MAX_CHARS} znaków` });
export const TASK_CLIENT_MESSAGE_AUTHORS = ['team', 'client'] as const;
export type TaskClientMessageAuthor = (typeof TASK_CLIENT_MESSAGE_AUTHORS)[number];

export const listTaskClientMessagesInput = z.object({ taskId: idSchema }).strict();
export const addTaskClientMessageInput = z.object({ taskId: idSchema, text: taskClientMessageTextSchema }).strict();
export type AddTaskClientMessageInput = z.input<typeof addTaskClientMessageInput>;

export interface TaskClientMessageDto {
  id: string;
  author: TaskClientMessageAuthor;
  /** osoba (zespół albo osoba klienta); null = konto usunięte z widoku */
  person: TaskPerson | null;
  text: string;
  createdAt: Date;
}

export interface TaskClientConversation {
  taskId: string;
  /** zgłoszenie z portalu klienta (rozmowa dostępna) */
  portal: boolean;
  /** osoby klienta, które dostaną mail o odpowiedzi (aktywne w portalu) */
  clientPeople: { userId: string; name: string }[];
  messages: TaskClientMessageDto[];
  /** czy osoba może pisać do klienta (edycja zadania, zgłoszenie nie w archiwum) */
  canWrite: boolean;
}
